import { CircularProgress } from '@mui/material'
import styles from './Dashboard.module.scss'
import { type ResourceType } from '@src/pages/layout/Menu.tsx'
import { Link, useLocation } from 'react-router-dom'
import { LxIcon } from '@components/icon/Icon.tsx'
import { useUserContext } from '@logic/contexts/AppStore/UserContext.tsx'
import { Suspense, useEffect } from 'react'
import { useDocumentTitle } from '@src/hooks/useDocumentTitle'
import { ActivitiesIcon, AnalyticsIcon, CurriculumIcon, StudentsIcon, TeamMembersIcon } from '@src/assets/icons'
import PrivacyPopup from './PrivacyPopup'
import { checkAccSettingsScope } from '@src/utils/accountSettings'
import { useModal } from '@src/logic/contexts/Modal/ModalContext'
import { useDataProvider } from 'react-admin'
import { checkUserPolls, checkLearnerPolls } from '@src/pages/Poll/DashboardPoll'

export const getDashboardMenu = (currentUser: any): ResourceType[] => {
  const isParentRole = currentUser?.isParent
  const isAdminRole = ['admin', 'account_admin'].includes(currentUser?.userRole)

  return [
    { name: 'Learners', type: 'Learner', icon: StudentsIcon },
    { name: 'Team members', type: 'User', icon: TeamMembersIcon },
    { name: 'Activities', type: 'CurriculumView', icon: ActivitiesIcon },
    // Conditionally include 'Group Curriculums' if the user is not a parent
    !isParentRole && { name: 'Curriculums', type: 'GroupCurriculums', icon: CurriculumIcon },
    isAdminRole && { name: 'Analytics', type: 'Analytics', icon: AnalyticsIcon },
  ].filter(Boolean) as ResourceType[] // Filter out any false/null values
}

const Dashboard: React.FC = () => {
  useDocumentTitle('Dashboard')
  const { currentUser, isUserLoading, currentUserPolls } = useUserContext() as any
  const DashboardSubmenu = getDashboardMenu(currentUser)
  const { showModal } = useModal()
  const dataProvider = useDataProvider()
  const location = useLocation()

  const { canAccess: canSeePrivacyPopup, effectiveValue } = checkAccSettingsScope('privacy_notice')
  let accHasPrivacyPopUp = effectiveValue === 'true'

  useEffect(() => {
    if (canSeePrivacyPopup && accHasPrivacyPopUp) {
      const hasShownPopup = localStorage.getItem('hasShownPrivacyPopup')
      if (!hasShownPopup) {
        showModal(<PrivacyPopup />)
        localStorage.setItem('hasShownPrivacyPopup', 'true')
      }
    }
  }, [canSeePrivacyPopup, accHasPrivacyPopUp, showModal])

  useEffect(() => {
    if (isUserLoading || !currentUser) return

    ;(async () => {
      try {
        const learnerShown = await checkLearnerPolls(currentUser, dataProvider, showModal)
        if (!learnerShown) {
          checkUserPolls(currentUser, currentUserPolls, showModal)
        }
      } catch (e) {
        console.error('Error checking polls:', e)
      }
    })()
  }, [location.key, currentUser?.id, isUserLoading])

  return (
    <>
      <div className={styles.dashboardContainer}>
        <h2>Welcome {isUserLoading ? <CircularProgress /> : currentUser?.name}</h2>
        <Suspense fallback={<CircularProgress />}>
          {DashboardSubmenu.map((subItem: ResourceType) => (
            <div key={`Navigation-row-${subItem.name}`} className={styles.navigationRowContainer}>
              {' '}
              {
                <Link key={subItem.type} to={subItem.type}>
                  <div className={styles.navigationRow}>
                    {subItem?.icon ? <LxIcon sxStyles={{ height: '48px', width: '48px' }} icon={subItem.icon}></LxIcon> : null}
                    <span> {subItem.name} </span>
                  </div>
                </Link>
              }
            </div>
          ))}
        </Suspense>
      </div>
    </>
  )
}

export default Dashboard
